import { useMessages } from '@/features/language/use-language'
import { Notice } from '@/shared/ui/Notice'
import { OutlineButton } from '@/shared/ui/OutlineButton'
import { ProviderMark } from '@/shared/ui/ProviderMark'
import type { ProviderId } from '@/shared/ui/providers'
import type { LoginMethod } from './types'

interface LoginMethodSectionProps {
  methods: LoginMethod[]
  /** 목록을 읽지 못했으면 안내 한 줄만 서고 목록은 서지 않는다. */
  loadFailed: boolean
  /** 돌아온 주소가 나른 결과. 목록 바로 위 한 자리에만 선다. */
  notice: string | null
  /** provider로 떠나는 중인 항목. 나머지 추가 버튼도 함께 잠근다. */
  leaving: ProviderId | null
  onAdd: (provider: ProviderId) => void
  disabled: boolean
}

/**
 * 어느 버튼으로 이 공간에 들어올 수 있는지 보이는 자리.
 *
 * 여는 provider 전부가 한 목록에 서고 항목마다 상태만 갈린다. 추가한 것과 추가하지 않은 것을
 * 두 목록으로 가르지 않는다. 가르면 아래 목록이 할 일 목록처럼 읽혀 재촉이 된다.
 *
 * 항목의 차례는 서버가 준 그대로이고 상태에 따라 움직이지 않는다. 추가한 항목을 위로 올리면
 * 사람마다 다른 순서를 보게 되고 같은 사람도 하나를 추가한 뒤 목록이 뒤바뀐다.
 *
 * 항목에 provider 계정의 이메일을 보이지 않는다. 이 목록이 답할 질문은 어느 버튼으로 들어올 수
 * 있느냐이지 어느 계정이냐가 아니다.
 *
 * 안내가 놓일 자리는 목록 바로 위 하나다. 항목마다 안내를 붙이지 않는다.
 *
 * 해제를 두지 않는다. 자리를 비워 두지도 않고 나중에 된다고 적지도 않는다. 없는 기능을 문구로
 * 약속하면 그것을 찾는 사람이 생긴다.
 */
export function LoginMethodSection({
  methods,
  loadFailed,
  notice,
  leaving,
  onAdd,
  disabled,
}: LoginMethodSectionProps) {
  const m = useMessages()

  return (
    <section className="flex flex-col gap-3">
      <h2 className="text-ink-muted text-sm">{m.settings.loginMethod.label}</h2>
      <p className="text-ink-muted text-[15px] leading-[1.7]">
        {m.settings.loginMethod.description}
      </p>

      {notice && <Notice>{notice}</Notice>}
      {loadFailed && <Notice>{m.settings.loginMethod.loadFailed}</Notice>}

      {methods.length > 0 && (
        <ul className="border-border rounded-card bg-surface flex flex-col overflow-hidden border">
          {methods.map((method, index) => (
            <li
              key={method.providerId}
              className={`flex min-h-16 items-center justify-between gap-3 px-4 py-2.5 ${
                index > 0 ? 'border-border/60 border-t' : ''
              }`}
            >
              <span className="text-ink flex items-center gap-3">
                <ProviderMark provider={method.providerId} />
                <span>{m.auth.provider[method.providerId]}</span>
              </span>
              {method.linked ? (
                /* 읽는 대상이지 멈추는 대상이 아니다. 탭은 추가 버튼에만 멈춘다. */
                <span
                  className="text-ink-muted shrink-0 text-sm"
                  aria-label={m.settings.loginMethod.linkedName(m.auth.provider[method.providerId])}
                >
                  {m.settings.loginMethod.added}
                </span>
              ) : (
                <OutlineButton
                  label={m.settings.loginMethod.addName(m.auth.provider[method.providerId])}
                  onClick={() => onAdd(method.providerId)}
                  disabled={disabled || leaving !== null}
                  busy={leaving === method.providerId}
                >
                  {leaving === method.providerId
                    ? m.auth.provider.leavingTo(m.auth.provider[method.providerId])
                    : m.settings.loginMethod.add}
                </OutlineButton>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
